import { cn } from "@utils/tw"
import { forwardRef } from "react"

type Breakpoint = "initial" | "bigPhone" | "tablet" | "laptop" | "desktop" | "cinema"

const BREAKPOINT_PREFIX: Record<Breakpoint, string> = {
  initial: "",
  bigPhone: "sm:",
  tablet: "md:",
  laptop: "lg:",
  desktop: "xl:",
  cinema: "xxl:",
}

type Sizes = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10

type SpacerProps = { [key in Breakpoint]?: Sizes }

/**
 * `VSpace`/`HSpace` build className strings dynamically (`${prefix}h-${size}`)
 * from props, which Tailwind's content scanner can't follow (it only picks up
 * classes that appear as literal text somewhere in a scanned file). This block
 * exists purely so every combination is guaranteed to have real CSS generated
 * for it — it's never read at runtime.
 */
export const __tailwindSpacerSafelist = `
  h-0 h-1 h-2 h-3 h-4 h-5 h-6 h-7 h-8 h-9 h-10
  sm:h-0 sm:h-1 sm:h-2 sm:h-3 sm:h-4 sm:h-5 sm:h-6 sm:h-7 sm:h-8 sm:h-9 sm:h-10
  md:h-0 md:h-1 md:h-2 md:h-3 md:h-4 md:h-5 md:h-6 md:h-7 md:h-8 md:h-9 md:h-10
  lg:h-0 lg:h-1 lg:h-2 lg:h-3 lg:h-4 lg:h-5 lg:h-6 lg:h-7 lg:h-8 lg:h-9 lg:h-10
  xl:h-0 xl:h-1 xl:h-2 xl:h-3 xl:h-4 xl:h-5 xl:h-6 xl:h-7 xl:h-8 xl:h-9 xl:h-10
  xxl:h-0 xxl:h-1 xxl:h-2 xxl:h-3 xxl:h-4 xxl:h-5 xxl:h-6 xxl:h-7 xxl:h-8 xxl:h-9 xxl:h-10
  w-0 w-1 w-2 w-3 w-4 w-5 w-6 w-7 w-8 w-9 w-10
  sm:w-0 sm:w-1 sm:w-2 sm:w-3 sm:w-4 sm:w-5 sm:w-6 sm:w-7 sm:w-8 sm:w-9 sm:w-10
  md:w-0 md:w-1 md:w-2 md:w-3 md:w-4 md:w-5 md:w-6 md:w-7 md:w-8 md:w-9 md:w-10
  lg:w-0 lg:w-1 lg:w-2 lg:w-3 lg:w-4 lg:w-5 lg:w-6 lg:w-7 lg:w-8 lg:w-9 lg:w-10
  xl:w-0 xl:w-1 xl:w-2 xl:w-3 xl:w-4 xl:w-5 xl:w-6 xl:w-7 xl:w-8 xl:w-9 xl:w-10
  xxl:w-0 xxl:w-1 xxl:w-2 xxl:w-3 xxl:w-4 xxl:w-5 xxl:w-6 xxl:w-7 xxl:w-8 xxl:w-9 xxl:w-10
`

function spacerClasses(axis: "h" | "w", props: SpacerProps) {
  return cn(
    ...(Object.keys(BREAKPOINT_PREFIX) as Breakpoint[])
      .filter((bp) => props[bp] !== undefined)
      .map((bp) => `${BREAKPOINT_PREFIX[bp]}${axis}-${props[bp]}`),
  )
}

export const VSpace = forwardRef<HTMLDivElement, SpacerProps>((props, ref) => (
  <div ref={ref} className={cn("h-0", spacerClasses("h", props))} />
))
VSpace.displayName = "VSpace"

export const HSpace = forwardRef<HTMLDivElement, SpacerProps>((props, ref) => (
  <div ref={ref} className={cn("w-0", spacerClasses("w", props))} />
))
HSpace.displayName = "HSpace"
